import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = [
  "General",
  "Mental Health — Manas",
  "Women's Health & Well-being — Shakti",
  "Children & Conscious Parenting — Samskara",
  "Samatva Weekly — Tattva Bodh",
  "Vaarta — Stories & Podcast",
  "Food Beyond Nourishment — Ahaar",
  "Spirituality & Indian Wisdom",
];

export const JoinForm = () => {
  const [form, setForm] = useState({ name: "", email: "", interest: "General", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Welcome to the circle. We will write to you soon.");
      setForm({ name: "", email: "", interest: "General", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full bg-transparent border-0 border-b border-samatva-ink/25 rounded-none px-0 py-3 text-base font-light text-samatva-ink placeholder:text-samatva-muted/60 focus:outline-none focus:border-samatva-plum transition-colors duration-300";

  return (
    <section
      id="join"
      data-testid="join-section"
      className="py-28 md:py-40 px-6 md:px-12 max-w-[1400px] mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <p className="text-xs tracking-[0.35em] uppercase text-samatva-plum mb-6">
            Join the Community
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-samatva-ink leading-[1.05]">
            Come sit <em className="text-samatva-plum">with us.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-samatva-muted max-w-md">
            Whether you are seeking healing, conscious parenting, growing
            spiritually, or simply breathing — there is a place for you here.
            Tell us a little about yourself and we will reach out.
          </p>
          <div className="mt-10 overflow-hidden rounded-[2.5rem] aspect-[16/9] max-w-md">
            <img
              src={IMAGES.food}
              alt="A circle of women in conversation at golden hour in the mountains"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="join-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 bg-samatva-lavender/30 border border-samatva-line rounded-[2.5rem] p-8 md:p-12 flex flex-col gap-8"
        >
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
              Your name
            </label>
            <input
              data-testid="join-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Full name"
              className={field}
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
              Email
            </label>
            <input
              data-testid="join-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              className={field}
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
              What calls to you?
            </label>
            <select
              data-testid="join-interest-select"
              value={form.interest}
              onChange={set("interest")}
              className={`${field} cursor-pointer`}
            >
              {INTERESTS.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-samatva-plum">
              A few words (optional)
            </label>
            <textarea
              data-testid="join-message-input"
              rows={3}
              value={form.message}
              onChange={set("message")}
              placeholder="What brings you to Samatva?"
              className={`${field} resize-none`}
            />
          </div>
          <button
            data-testid="join-submit-button"
            type="submit"
            disabled={sending}
            className="group mt-2 self-start px-10 py-4 rounded-full bg-samatva-ink text-samatva-cream text-sm tracking-wide hover:bg-samatva-plum disabled:opacity-60 transition-colors duration-300 flex items-center gap-2"
          >
            {sending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            )}
            {sending ? "Sending…" : "Join Samatva"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
